import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Card,
  Divider,
  Grid,
  Paper,
  Box,
} from "@mui/material";

const EmployeeWorkHoursReport = ({ data }) => {
  

  if (!data) {
    return (
      <Card sx={{ padding: 4, textAlign: "center", margin: "16px" }}>
        <Typography variant="h6">
          No data available to display the report.
        </Typography>
      </Card>
    );
  } 

  const { filters, workingDays, employeeStats, grandTotal } = data;
  return (
    <>
      {/* Date Range */}
      <Typography variant="subtitle1" sx={{ marginBottom: 2 }}>
        <strong>Report Period:</strong> {filters.dateRange.start} to{" "}
        {filters.dateRange.end}
      </Typography>

      <Grid
        container
        spacing={1}
        sx={{
          marginBottom: 4,
          justifyContent: "space-between", // Ensures even spacing
          flexWrap: "wrap", // Forces all boxes to remain in a single row
          overflowX: "auto", // Adds horizontal scroll for smaller screens
        }}
      >
        {/* Total Weekdays */}
        <Grid item sx={{ flex: "1 1 auto", maxWidth: "18%" }}>
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              padding: 2,
              borderRadius: 2,
              boxShadow: 1,
              textAlign: "center",
              height: "120px", // Fixed height
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
              Total Weekdays
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", color: "#007BFF" }}
            >
              {workingDays.weekdays}
            </Typography>
          </Box>
        </Grid>

        {/* Total Saturdays */}
        <Grid item sx={{ flex: "1 1 auto", maxWidth: "18%" }}>
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              padding: 2,
              borderRadius: 2,
              boxShadow: 1,
              textAlign: "center",
              height: "120px", // Fixed height
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
              Total Saturdays
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", color: "#28A745" }}
            >
              {workingDays.saturdays}
            </Typography>
          </Box>
        </Grid>

        {/* Total Expected Hours */}
        {localStorage.getItem("empRole") === "101" && (<Grid item sx={{ flex: "1 1 auto", maxWidth: "18%" }}>
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              padding: 2,
              borderRadius: 2,
              boxShadow: 1,
              textAlign: "center",
              height: "120px", // Fixed height
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
              Total Expected Minutes
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", color: "#FFC107" }}
            >
              {grandTotal.totalExpectedHours}
            </Typography>
          </Box>
        </Grid>)}

        {/* Total Worked Hours */}
        <Grid item sx={{ flex: "1 1 auto", maxWidth: "18%" }}>
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              padding: 2,
              borderRadius: 2,
              boxShadow: 1,
              textAlign: "center",
              height: "120px", // Fixed height
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
              Total Duration
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", color: "#DC3545" }}
            >
              {grandTotal.totalWorkedHours}
            </Typography>
          </Box>
        </Grid>

        {/* Attendance Percentage */}
        <Grid item sx={{ flex: "1 1 auto", maxWidth: "18%" }}>
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              padding: 2,
              borderRadius: 2,
              boxShadow: 1,
              textAlign: "center",
              height: "120px", // Fixed height
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
              Attendance Percentage
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", color: "#17A2B8" }}
            >
              {grandTotal.totalWorkedPercentage}%
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ marginBottom: 4 }} />

      {/* Employee Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
        <Table
          sx={{
            minWidth: 650,
            tableLayout: "fixed",
            "& .MuiTableCell-root": {
              border: "1px solid rgba(224, 224, 224, 1)", // Gridlines
            },
          }}
          aria-label="professional table"
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: "#EEF0F4" }}>
              {[
                "Employee Name",
                "Total Weekdays",
                "Weekday Minutes",
                "Saturdays",
                "Saturday Minutes",
                "Total Expected Minutes",
                "Total Worked Minutes",
                "Total Incoming Calls",
                "Total Incoming Minutes",
                "Total Outgoing Calls",
                "Total Outgoing Minutes",
                "Work Percentage",
              ].map((header, index) => (
                <TableCell
                  key={index}
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    padding: "8px",
                    fontSize: "0.9rem",
                    color: "#333",
                    border: "1px solid rgba(224, 224, 224, 1)", // Header gridlines
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {employeeStats.map((employee, index) => (
              <TableRow
                key={index}
                sx={{
                  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F9FAFB", // Striped rows
                  "&:hover": { backgroundColor: "#F1F5F9" }, // Hover effect
                }}
              >
                <TableCell align="center">{employee.employeeName}</TableCell>
                <TableCell align="center">{employee.totalWeekdays}</TableCell>
                <TableCell align="center">
                  {employee.totalWeekdayHours}
                </TableCell>
                <TableCell align="center">{employee.saturdays}</TableCell>
                <TableCell align="center">
                  {employee.totalSaturdayHours}
                </TableCell>
                <TableCell align="center">
                  {employee.totalExpectedHours}
                </TableCell>
                <TableCell align="center">
                  {employee.totalWorkedHours}
                </TableCell>
                <TableCell align="center">
                  {employee.callDetails?.incoming?.count || "-"}
                </TableCell>
                <TableCell align="center">
                  {employee.callDetails?.incoming?.hours || "-"}
                </TableCell>
                <TableCell align="center">
                  {employee.callDetails?.outgoing?.count || "-"}
                </TableCell>
                <TableCell align="center">
                  {employee.callDetails?.outgoing?.hours || "-"}
                </TableCell>
                <TableCell align="center">
                  {employee.totalWorkedPercentage || "-"}%
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default EmployeeWorkHoursReport;
